import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isSessionPlaying } from "@/lib/sessionHost";

/**
 * Iter 92 — Automatic Morning Sessions.
 *
 * Two-layer scheduler (mounted globally inside the Router):
 * 1. OVERNIGHT — the user fell asleep with Continua open; at (wake-up − 5 min)
 *    the morning session starts by itself.
 * 2. ON-OPEN FALLBACK — if the OS killed the tab overnight, opening the app
 *    inside the morning window (wake −5 min → wake +45 min) starts it
 *    immediately.
 *
 * All eligibility rules (enabled, recent night, one-morning-per-night,
 * free-tier trials) live server-side in GET /api/morning/eligibility.
 * A trial is only consumed once playback actually starts (backend contract).
 */
const WINDOW_BEFORE_MS = 5 * 60 * 1000;
const WINDOW_AFTER_MS = 45 * 60 * 1000;
const CHECK_EVERY_MS = 30 * 1000;

export const MorningAutoStart = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const firingRef = useRef(false);
  const locRef = useRef(loc.pathname);
  locRef.current = loc.pathname;

  useEffect(() => {
    if (!user?.morning_enabled || !user?.wake_time) return undefined;

    const check = async () => {
      if (firingRef.current) return;
      // Never interrupt LIVE playback (an ended session screen is fine).
      if (isSessionPlaying()) return;
      const [h, m] = String(user.wake_time).split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return;
      const now = new Date();
      const wake = new Date(now);
      wake.setHours(h, m, 0, 0);
      const t = now.getTime();
      if (t < wake.getTime() - WINDOW_BEFORE_MS || t > wake.getTime() + WINDOW_AFTER_MS) return;
      // One launch per calendar day per browser, even across refreshes.
      const dayKey = `continua_morning_started_${now.toISOString().slice(0, 10)}`;
      if (localStorage.getItem(dayKey)) return;
      firingRef.current = true;
      try {
        const { data } = await api.get("/morning/eligibility");
        if (!data?.eligible) {
          firingRef.current = false; // conditions may change — keep polling the window
          return;
        }
        try { localStorage.setItem(dayKey, "1"); } catch (e) { /* ignore */ }
        nav("/waiting", {
          state: {
            launchId: Date.now(),
            mode: "sleep",
            kind: "morning",
            wakeTime: data.wake_time,
            durationMinutes: data.duration_minutes || 10,
            atmosphereId: data.atmosphere_id,
            atmoName: data.atmosphere_name || "the quiet place",
            characterId: data.character_id,
            generateMorning: false,
            autoStarted: true,
          },
        });
      } catch (e) {
        firingRef.current = false; // transient network error — retry next tick
      }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    // Iter 96 — native: run a check the moment the app returns to foreground
    // (dispatched by the Capacitor lifecycle bridge; never fires on web).
    window.addEventListener("continua:app-active", check);
    return () => {
      clearInterval(timer);
      window.removeEventListener("continua:app-active", check);
    };
  }, [user?.morning_enabled, user?.wake_time]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default MorningAutoStart;
